import { RowCrudStatus } from "../../common/types";
import {
  CreditAccount,
  CreditAccountUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { creditTierDisplayId } from "../../creditTier/store/CreditTierCommonStore";
import {
  CreditMovementValuesType,
  getCreditMovementValues,
} from "../../creditMovement/store/CreditMovementCommonStore";

export type CreditAccountValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  balance: number;
  __tier: string;
  showTier: { displayId: string; id: string } | null;
  showMovements: CreditMovementValuesType[];
};

export type CreditAccountErrorsType = {
  balance?: { message: string; fieldLabel: string };
  tier?: { message: string; fieldLabel: string };
  movements?: { message: string; fieldLabel: string };
};

export function CreditAccountValuesInit(): CreditAccountValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    balance: 0,
    __tier: "",
    showTier: null,
    showMovements: [],
  };
}

export type CreditAccountType = CreditAccount | CreditAccountValuesType;

export function creditAccountDisplayId(
  source: Partial<CreditAccount>,
  _langStore: LangStore
) {
  return source.balance?.toString() ?? _langStore.emptyStringSymbol;
}

export function getCreditAccountValues(
  source: CreditAccountType,
  _langStore: LangStore,
  _duplicated: boolean = false
): CreditAccountValuesType {
  const entityValues = CreditAccountValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : creditAccountDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.balance = source.balance || 0;
  entityValues.showTier =
    "tier" in source && source.tier
      ? {
          id: source.tier.id,
          displayId: creditTierDisplayId(source.tier, _langStore),
        }
      : "showTier" in source && source.showTier
      ? { id: source.showTier.id, displayId: source.showTier.displayId }
      : null;
  entityValues.__tier = entityValues.showTier?.displayId || "";
  entityValues.showMovements =
    "movements" in source && source.movements
      ? (source.movements || []).map((x) => {
          const row: CreditMovementValuesType = getCreditMovementValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showMovements" in source && source.showMovements
      ? (source.showMovements || []).map((x) => {
          const row: CreditMovementValuesType = getCreditMovementValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getCreditAccountUpdateVariables(
  values: CreditAccountValuesType
): CreditAccountUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    tierId: values.showTier?.id || "",
  };
}
